import {useEffect, useState, useRef} from 'react';
import {Link, useOutlet} from 'react-router-dom';
import {useDispatch} from 'react-redux';

import {addMicroservice, clearMicroservices} from '../../features/microservices.slice';
import Context from './Context';
import _axios from '../../utils/axios';
import _kc from '../../utils/keycloak';
import {GROUP_ADMIN} from '../../utils/settings';
import validate from '../../utils/validator';
import {handleError} from '../../utils/utils';

import styles from '../../styles/base_components/Navbar.module.scss';
import styled from "styled-components";

const Content = styled.div`
  &&& {
    ${props => props.displayContent && 'display: block;'}
  }
`;

function Navbar(props) {
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState({});

  const [dropdownItems, setDropdownItems] = useState([]);
  const [displayContent, setDisplayContent] = useState(false);

  const outlet = useOutlet();
  const dispatch = useDispatch();

  const ref = useRef();

  function clickDropdownItem(item) {
    return event => {
      switch (item) {
        case 'ADMIN':
          window.location.href = '/admin';
          break;
        case 'GROUP ADMIN':
          window.location.href = '/group-admin';
          break;
        case 'USER':
          window.location.href = '/';
          break;
        default:
          _kc.logout();
          break;
      }
    };
  }

  function onClickContent(event) {
    setDisplayContent(displayContent => !displayContent);
  }

  useEffect(() => {  // get microservices and users
    dispatch(clearMicroservices());

    if (groups.length === 0) return;
    const group = groups[0];  // TODO only one group

    _axios.get(GROUP_ADMIN.MICROSERVICES_PATH + '?group=' + group)
      .then(response => {
        const {ok, data} = response.data;
        if (typeof ok !== 'number' || !Array.isArray(data)) throw new Error('The microservices from the database do not correspond to our standard.');
        if (ok !== 1) throw new Error('The database path to microservices is currently unavailable.');
        if (data.length === 0) return;

        let error = '';
        for (let microservice of data) {
          let err = validate([
            {value: microservice.id, type: 'db_microservice_id'},
            {value: microservice.fetchers, type: 'db_microservice_fetchers'},
            {value: microservice.status, type: 'db_microservice_status', empty: true},
            {value: microservice.host, type: 'db_microservice_host'},
            {value: microservice.uri, type: 'db_microservice_uri'},
            {value: microservice.version, type: 'db_microservice_version'},
            {value: microservice.groups, type: 'db_microservice_groups'},
            {value: microservice.author, type: 'db_microservice_author'},
            {value: microservice.name, type: 'db_microservice_name'},
            {value: microservice.created_at, type: 'db_microservice_CreatedAt'},
            {value: microservice.updated_at, type: 'db_microservice_UpdatedAt'}
          ]);
          err = err.filter(e => e !== '');

          if (err.length !== 0) error = error + 'Error at validating ' + microservice.host + ': ' + err.join('. ') + '\n';
          else dispatch(addMicroservice(microservice));
        }
        if (error) throw new Error(error.slice(0, error.length - 2));
      })
      .catch(handleError);
    _axios.get(GROUP_ADMIN.USERS_PATH + '?group=' + group)
      .then(response => {
        const {ok, data} = response.data;
        if (typeof ok !== 'number' || typeof data !== 'object' || !Array.isArray(data)) throw new Error('The users from the database do not correspond to our standard');
        if (ok !== 1) throw new Error('The database path to users is currently unavailable.');
        if (data.length === 0) return;

        let error = '';
        let u = {};
        for (let user of data) {
          let err = validate([
            {value: user.id, type: 'db_user_id'},
            {value: user.username, type: 'db_user_username'},
            {value: user.email, type: 'db_user_email'},
            {value: user.groups, type: 'db_user_groups'},
            {value: user.roles, type: 'db_user_roles'},
            {value: user.enabled, type: 'db_user_enabled', empty: true}
          ]);
          err = err.filter(e => e !== '');

          if (err.length !== 0) error = error + 'Error at validating ' + user.username + ' ' + user.email + ': ' + err.join('. ') + '\n';
          else setUsers(users => {
            let newUser = {};
            newUser[user.id] = {
              username: user.username,
              email: user.email,
              groups: user.groups,
              roles: user.roles,
              enabled: user.enabled
            };
            return Object.assign(newUser, users);
          });
        }
        if (error) throw new Error(error.slice(0, error.length - 2));
      })
      .catch(handleError);
  }, [groups, dispatch]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setDisplayContent(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref]);

  useEffect(() => {  // get groups
    const roles = _kc.tokenParsed.realm_access.roles;
    let g = [];
    for (let role of roles) {
      if (role === 'guests_admin') continue;  // TODO delete this line

      const i = role.search(/_admin/);
      if (i === -1) continue;
      g.push(role.substring(0, i));
    }
    setGroups(g);

    const r = roles.filter(role => role.search(/_admin/) !== -1);
    let di = [];
    if (r.find(role => role === 'guests_admin')) di.push('ADMIN');
    if (r.find(role => role !== 'guests_admin')) di.push('GROUP ADMIN');
    di.push('USER');
    di.push('LOGOUT');
    setDropdownItems(di);
  }, []);
  
  return (
    <Context.Provider value={{
      users: users,
      setUsers: setUsers,
      groups: groups,
      setGroups: setGroups,
      role: GROUP_ADMIN,
      defaultGroup: groups[0]
    }}>
      <div className={styles.navbar}>
        <Link to='/group-admin/microservices'>microservices</Link>
        <Link to='/group-admin'>users</Link>

        <div className={styles.dropdown} ref={ref} onClick={onClickContent}>
          <div/>
          <Content className={styles.content} displayContent={displayContent}>
            {dropdownItems.map(item => (
              <p key={item} onClick={clickDropdownItem(item)}>{item}</p>
            ))}
          </Content>
        </div>
      </div>

      {outlet}
    </Context.Provider>
  );
}

export default Navbar;